import {
	Box,
	Breadcrumbs,
	Button,
	FormControl,
	FormControlLabel,
	Grid,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Radio,
	RadioGroup,
	Select,
	Stack,
	TextField,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import {
	BtnBox,
	PageBg2,
	PageWrap,
	SearchRowItem,
	TitleText,
} from 'components/FrameworkStyle'
import { getSettings } from 'helpers/storage'
import { selectuserInfo } from 'hooks/userInfo'
import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import ClientSearchPopup from 'views/test/clientSearchPopup'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/ko'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import moment from 'moment'
import 'moment/locale/ko'
import { alertModal, compareDate, isValidDateType } from 'util/util'
import DownloadIcon from '@mui/icons-material/Download'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import ContentPasteSearchTwoToneIcon from '@mui/icons-material/ContentPasteSearchTwoTone'
import { PageBg } from 'components/ComponentStyles'
import 'dayjs/locale/ko'
import dayjs, { Dayjs } from 'dayjs'
import {
	DataGridPremium,
	GridColDef,
	GridColumnGroupingModel,
	GridDensity,
	GridSelectionModel,
	GridToolbar,
	koKR,
	useGridApiRef,
} from '@mui/x-data-grid-premium'
import { apiCultureMicroAntiList } from 'api/api'
import ExcelJS from 'exceljs'

// API 응답 타입 (snake_case - DB에서 직접 온 형태)
type ApiResponseType = {
	rowNum?: number
	rownum?: number
	eqpMicroNm?: string
	eqp_micro_nm?: string
	EQP_MICRO_NM?: string
	eqpAntiNm?: string
	eqp_anti_nm?: string
	EQP_ANTI_NM?: string
	microTotal?: number
	micrototal?: number
	MicroTotal?: number
	sRate?: string
	s_rate?: string
	S_Rate?: string
	iRate?: string
	i_rate?: string
	I_Rate?: string
	rRate?: string
	r_rate?: string
	R_Rate?: string
	nRate?: string
	n_rate?: string
	pRate?: string
	p_rate?: string
	minusRate?: string
	minus_rate?: string
	plusRate?: string
	plus_rate?: string
	dotRate?: string
	dot_rate?: string
	success?: number
	message?: string
}

// 필드 값 가져오기 헬퍼 함수
const getFieldValue = (item: ApiResponseType, ...keys: string[]): any => {
	for (const key of keys) {
		if ((item as any)[key] !== undefined) {
			return (item as any)[key]
		}
	}
	return undefined
}

// 변환된 Row 타입
type TransformedRowType = {
	id: number
	eqpMicroNm: string
	microTotal: number
	[key: string]: string | number // 동적 컬럼을 위한 인덱스 시그니처
}

// 데이터 변환 함수: API 응답을 그리드에 맞게 변환
const transformData = (
	data: ApiResponseType[],
): {
	columns: GridColDef[]
	groupingModel: GridColumnGroupingModel
	rows: TransformedRowType[]
} => {
	if (!data || data.length === 0) {
		return {
			columns: [
				{
					field: 'eqpMicroNm',
					headerName: '균주명',
					width: 200,
					headerAlign: 'center',
				},
				{
					field: 'microTotal',
					headerName: '전체건수',
					width: 80,
					headerAlign: 'center',
				},
			],
			groupingModel: [],
			rows: [],
		}
	}

	// 필드명 정규화 함수
	const getMicroName = (item: ApiResponseType) =>
		getFieldValue(item, 'eqpMicroNm', 'eqp_micro_nm', 'EQP_MICRO_NM') || ''
	const getAntiName = (item: ApiResponseType) =>
		getFieldValue(item, 'eqpAntiNm', 'eqp_anti_nm', 'EQP_ANTI_NM') || ''
	const getMicroTotal = (item: ApiResponseType) =>
		getFieldValue(item, 'microTotal', 'micrototal', 'MicroTotal') || 0
	const getSRate = (item: ApiResponseType) =>
		getFieldValue(item, 'sRate', 's_rate', 'S_Rate') || ''
	const getIRate = (item: ApiResponseType) =>
		getFieldValue(item, 'iRate', 'i_rate', 'I_Rate') || ''
	const getRRate = (item: ApiResponseType) =>
		getFieldValue(item, 'rRate', 'r_rate', 'R_Rate') || ''
	const getNRate = (item: ApiResponseType) =>
		getFieldValue(item, 'nRate', 'n_rate', 'N_Rate') || ''
	const getPRate = (item: ApiResponseType) =>
		getFieldValue(item, 'pRate', 'p_rate', 'P_Rate') || ''
	const getMinusRate = (item: ApiResponseType) =>
		getFieldValue(item, 'minusRate', 'minus_rate', 'Minus_Rate') || ''
	const getPlusRate = (item: ApiResponseType) =>
		getFieldValue(item, 'plusRate', 'plus_rate', 'Plus_Rate') || ''
	const getDotRate = (item: ApiResponseType) =>
		getFieldValue(item, 'dotRate', 'dot_rate', 'Dot_Rate') || ''

	// 1. 고유한 항생제명(EQP_ANTI_NM) 목록 추출
	const uniqueAntiNames = Array.from(
		new Set(data.map((item) => getAntiName(item))),
	)

	// 2. 고유한 균주명(EQP_MICRO_NM) 목록 추출
	const uniqueMicroNames = Array.from(
		new Set(data.map((item) => getMicroName(item))),
	)

	// 3. 동적 컬럼 생성
	const baseColumns: GridColDef[] = [
		{
			field: 'eqpMicroNm',
			headerName: '균주명',
			minWidth: 300,
			headerAlign: 'center',
			headerClassName: 'basic-info-header',
		},
		{
			field: 'microTotal',
			headerName: '전체건수',
			width: 100,
			headerAlign: 'center',
			type: 'number',
			headerClassName: 'basic-info-header last-basic-header',
		},
	]

	const dynamicColumns: GridColDef[] = []
	const groupingModel: GridColumnGroupingModel = []

	// 각 항생제별로 어떤 Rate 컬럼에 값이 있는지 확인
	const antiRateHasValue: Map<
		string,
		{
			s: boolean
			i: boolean
			r: boolean
			n: boolean
			p: boolean
			minus: boolean
			plus: boolean
			dot: boolean
		}
	> = new Map()

	uniqueAntiNames.forEach((antiName) => {
		const antiData = data.filter((item) => getAntiName(item) === antiName)
		antiRateHasValue.set(antiName, {
			s: antiData.some((item) => getSRate(item) !== ''),
			i: antiData.some((item) => getIRate(item) !== ''),
			r: antiData.some((item) => getRRate(item) !== ''),
			n: antiData.some((item) => getNRate(item) !== ''),
			p: antiData.some((item) => getPRate(item) !== ''),
			minus: antiData.some((item) => getMinusRate(item) !== ''),
			plus: antiData.some((item) => getPlusRate(item) !== ''),
			dot: antiData.some((item) => getDotRate(item) !== ''),
		})
	})

	const visibleAntiNames: string[] = []

	uniqueAntiNames.forEach((antiName, index) => {
		const groupId = `anti_${index}`
		const hasValue = antiRateHasValue.get(antiName)!

		const rateColumns: {
			field: string
			headerName: string
			key: keyof typeof hasValue
		}[] = [
			{ field: `${groupId}_s`, headerName: 'S', key: 's' },
			{ field: `${groupId}_i`, headerName: 'I', key: 'i' },
			{ field: `${groupId}_r`, headerName: 'R', key: 'r' },
			{ field: `${groupId}_n`, headerName: 'N', key: 'n' },
			{ field: `${groupId}_p`, headerName: 'P', key: 'p' },
			{ field: `${groupId}_minus`, headerName: '-', key: 'minus' },
			{ field: `${groupId}_plus`, headerName: '+', key: 'plus' },
			{ field: `${groupId}_dot`, headerName: '.', key: 'dot' },
		]

		const visibleColumns = rateColumns.filter((col) => hasValue[col.key])

		// 값이 있는 컬럼만 추가
		visibleColumns.forEach((col, colIndex) => {
			dynamicColumns.push({
				field: col.field,
				headerName: col.headerName,
				width: 150,
				headerAlign: 'center',
				align: 'center',
				headerClassName: `dynamic-header dynamic-header-${
					colIndex === 0
						? 'first'
						: colIndex === visibleColumns.length - 1
							? 'last'
							: 'middle'
				}`,
			})
		})

		// 그룹 모델에도 값이 있는 컬럼만 추가
		if (visibleColumns.length > 0) {
			visibleAntiNames.push(antiName)
			groupingModel.push({
				groupId: groupId,
				headerName: antiName,
				headerAlign: 'center',
				headerClassName: 'biotic-header',
				children: visibleColumns.map((col) => ({ field: col.field })),
			})
		}
	})

	if (groupingModel.length > 0) {
		groupingModel.unshift({
			groupId: 'basicInfo',
			headerName: '기본정보',
			headerAlign: 'center',
			headerClassName: 'basic-header',
			children: [{ field: 'eqpMicroNm' }, { field: 'microTotal' }],
		})

		for (let index = 1; index < groupingModel.length; index += 1) {
			const position =
				index === 1
					? 'first'
					: index === groupingModel.length - 1
						? 'last'
						: 'middle'
			groupingModel[index].headerClassName =
				`biotic-header group-biotic-border-${position}`
		}
	}

	// 4. Row 데이터 변환 (균주명 기준으로 피벗)
	const rows: TransformedRowType[] = uniqueMicroNames.map(
		(microName, rowIndex) => {
			// 해당 균주의 데이터 필터링
			const microData = data.filter((item) => getMicroName(item) === microName)
			const microTotal = getMicroTotal(microData[0])

			const row: TransformedRowType = {
				id: rowIndex + 1,
				eqpMicroNm: microName,
				microTotal: microTotal,
			}

			// 각 항생제별 S, I, R, N, P, -, +, . 값 매핑
			uniqueAntiNames.forEach((antiName, antiIndex) => {
				const groupId = `anti_${antiIndex}`
				const antiData = microData.find((item) => getAntiName(item) === antiName)

				row[`${groupId}_s`] = antiData ? getSRate(antiData) : ''
				row[`${groupId}_i`] = antiData ? getIRate(antiData) : ''
				row[`${groupId}_r`] = antiData ? getRRate(antiData) : ''
				row[`${groupId}_n`] = antiData ? getNRate(antiData) : ''
				row[`${groupId}_p`] = antiData ? getPRate(antiData) : ''
				row[`${groupId}_minus`] = antiData ? getMinusRate(antiData) : ''
				row[`${groupId}_plus`] = antiData ? getPlusRate(antiData) : ''
				row[`${groupId}_dot`] = antiData ? getDotRate(antiData) : ''
			})

			return row
		},
	)

	return {
		columns: [...baseColumns, ...dynamicColumns],
		groupingModel,
		rows,
	}
}

const locales = ['ko'] as const

const ByStrain = () => {
	const theme = useTheme()
	const isMdUp = useMediaQuery(theme.breakpoints.up('md'))
	const isLgUp = useMediaQuery(theme.breakpoints.up('lg'))
	const userInfo = useSelector(selectuserInfo)
	const [locale, setLocale] = useState<(typeof locales)[number]>('ko')

	const env = getSettings()
	const [disabledSave, setDisabledSave] = useState(true)
	const [disabledDel, setDisabledDel] = useState(true)
	const [disabledSearch, setDisabledSearch] = useState(false)
	const [disabledReset, setDisabledReset] = useState(false)

	const [dateRange, setDateRange] = useState('1m')

	const [search, setSearch] = useState({
		clientID: userInfo.infoType === 'C' ? userInfo.clientID : 0,
		startDate: moment(new Date()).isBefore('2023-08')
			? '2023-08'
			: moment().subtract(1, 'months').format('YYYY-MM'),
		endDate: moment().format('YYYY-MM'),
		searchType: 'R',
		eqpMicroNm: '0',
		antiName: '0',
		risName: '0',
		isAdmin: userInfo.infoType === 'C' ? 0 : 1,
	})

	const [eqpMicroList, setEqpMicroList] = useState<
		Array<{ eqpMicroNm: string }>
	>([])
	const [antiNameList, setAntiNameList] = useState<Array<{ antiName: string }>>(
		[],
	)
	const [risNameList, setRisNameList] = useState<Array<{ risName: string }>>([])

	const onKeyUp = (e: any) => {
		if (e.keyCode === 13) actionSearch()
	}

	const actionSearch = () => {
		const startMonth = moment(search.startDate, ['YYYY-MM', 'YYYY-MM-DD'], true)
		const endMonth = moment(search.endDate, ['YYYY-MM', 'YYYY-MM-DD'], true)

		if (!startMonth.isValid() || !endMonth.isValid()) {
			alertModal('날짜 형식이 올바르지 않습니다.', 'error', () => {})
			return
		}

		if (endMonth.isBefore(startMonth, 'month')) {
			alertModal('종료일이 시작일보다 작습니다.', 'error', () => {})
			return
		}

		if (
			endMonth.startOf('month').diff(startMonth.startOf('month'), 'months') > 12
		) {
			alertModal('검색기간은 1년을 초과할 수 없습니다.', 'error', () => {})
			return
		}

		if (search.clientID === 0 || search.clientID === null) {
			alertModal('거래처를 선택해주세요.', 'error', () => {})
			return
		}

		setGridMstLoading(true)

		const request = {
			...search,
			eqpMicroNm: search.eqpMicroNm === '0' ? '' : search.eqpMicroNm,
			antiName: search.antiName === '0' ? '' : search.antiName,
			risName: search.risName === '0' ? '' : search.risName,
		}

		apiCultureMicroAntiList(request)
			.then((res: any) => {
				console.log('API 응답:', res)

				if (res.length === 0) {
					setColumns([])
					setColumnGroupingModel([])
					setRows([])
				}

				if (res[0]?.success === 0 && res[0]?.message) {
					alertModal(
						res[0]?.message || '조회 중 오류가 발생했습니다.',
						'error',
						() => {},
					)
					setColumns([])
					setColumnGroupingModel([])
					setRows([])
					return
				} else {
					console.log(
						'첫번째 항목 키:',
						res && res.length > 0 ? Object.keys(res[0]) : 'empty',
					)
					const {
						columns: newColumns,
						groupingModel,
						rows: newRows,
					} = transformData(res)
					console.log('변환된 rows:', newRows)
					console.log('변환된 columns:', newColumns)
					console.log('변환된 groupingModel:', groupingModel)
					setColumns(newColumns)
					setColumnGroupingModel(groupingModel)
					setRows(newRows)
					// res에서 균주명을 eqpMicroNm으로, 항생제명을 antiName으로, 내성명을 risName으로 각각 추출하여 상태로 설정
					const eqpMicroSet = new Set<string>()
					const antiNameSet = new Set<string>()
					const risNameSet = new Set<string>()
					res.forEach((item: ApiResponseType) => {
						const eqpMicroNm = getFieldValue(
							item,
							'eqpMicroNm',
							'eqp_micro_nm',
							'EQP_MICRO_NM',
						)
						const antiName = getFieldValue(
							item,
							'eqpAntiNm',
							'eqp_anti_nm',
							'EQP_ANTI_NM',
						)
						const risName = getFieldValue(item, 'S_Rate', 'I_Rate', 'R_Rate')
						if (eqpMicroNm) eqpMicroSet.add(eqpMicroNm)
						if (antiName) antiNameSet.add(antiName)
						if (risName) risNameSet.add(risName)
					})
					setEqpMicroList(
						Array.from(eqpMicroSet).map((eqpMicroNm) => ({ eqpMicroNm })),
					)
					setAntiNameList(Array.from(antiNameSet).map((antiName) => ({ antiName })))
					setRisNameList(Array.from(risNameSet).map((risName) => ({ risName })))
				}
			})
			.finally(() => {
				setGridMstLoading(false)
			})
	}

	const actionReset = () => {
		setSearch({
			clientID: userInfo.infoType === 'C' ? userInfo.clientID : 0,
			startDate: moment(new Date()).isBefore('2023-08')
				? '2023-08'
				: moment().subtract(1, 'months').format('YYYY-MM'),
			endDate: moment().format('YYYY-MM'),
			searchType: 'R',
			eqpMicroNm: '0',
			antiName: '0',
			risName: '0',
			isAdmin: userInfo.infoType === 'C' ? 0 : 1,
		})
	}

	const actionExcelDownload = async () => {
		setSelectionModel([])

		// 테두리 스타일 정의
		const borderStyle: Partial<ExcelJS.Borders> = {
			top: { style: 'thin', color: { argb: 'FF000000' } },
			left: { style: 'thin', color: { argb: 'FF000000' } },
			bottom: { style: 'thin', color: { argb: 'FF000000' } },
			right: { style: 'thin', color: { argb: 'FF000000' } },
		}
		const groupHeaderFill: ExcelJS.Fill = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFBDC3C7' },
		}
		const columnHeaderFill: ExcelJS.Fill = {
			type: 'pattern',
			pattern: 'solid',
			fgColor: { argb: 'FFE0E0E0' },
		}

		// 셀 너비 자동 조정 함수
		const autoFitColumns = (
			sheet: ExcelJS.Worksheet,
			colDefs: GridColDef[],
			data: any[],
		) => {
			colDefs.forEach((col, index) => {
				let maxLength = String(col.headerName || '').length
				data.forEach((row) => {
					const cellValue = String(row[col.field] ?? '')
					if (cellValue.length > maxLength) {
						maxLength = cellValue.length
					}
				})
				const column = sheet.getColumn(index + 1)
				column.width = Math.min(Math.max(maxLength * 1.5 + 2, 10), 50)
			})
		}

		// ExcelJS Workbook 생성
		const workbook = new ExcelJS.Workbook()
		const sheet = workbook.addWorksheet('균주별 항생제 통계')

		const exportColumns = columns.filter((col) => col.field !== 'rowNum')

		const fieldToGroupHeader = new Map<string, string>()
		const mapGroupHeaders = (group: any, headerName: string) => {
			group.children?.forEach((child: any) => {
				if (child.field) {
					fieldToGroupHeader.set(child.field, headerName)
					return
				}
				if (child.children) {
					mapGroupHeaders(child, headerName)
				}
			})
		}

		columnGroupingModel.forEach((group: any) => {
			mapGroupHeaders(group, group.headerName || '')
		})

		const groupHeaderValues = exportColumns.map(
			(col) => fieldToGroupHeader.get(col.field) || '',
		)

		const groupHeaderRow = sheet.addRow(groupHeaderValues)

		let mergeStart = 1
		for (let index = 1; index <= groupHeaderValues.length; index += 1) {
			const current = groupHeaderValues[index - 1]
			const next = groupHeaderValues[index]
			if (current !== next) {
				if (current && index > mergeStart) {
					sheet.mergeCells(1, mergeStart, 1, index)
				}
				mergeStart = index + 1
			}
		}

		groupHeaderRow.eachCell({ includeEmpty: true }, (cell) => {
			cell.border = borderStyle
			cell.alignment = { horizontal: 'center' }
			cell.fill = groupHeaderFill
			cell.font = { bold: true }
		})

		const headerRow = sheet.addRow(exportColumns.map((col) => col.headerName))
		headerRow.eachCell({ includeEmpty: true }, (cell) => {
			cell.border = borderStyle
			cell.alignment = { horizontal: 'center' }
			cell.fill = columnHeaderFill
			cell.font = { bold: true }
		})

		// 데이터 추가
		rows.forEach((row: any) => {
			const dataRow = sheet.addRow(
				exportColumns.map((col) => row[col.field] ?? ''),
			)
			dataRow.eachCell((cell) => {
				cell.border = borderStyle
			})
		})

		// 컬럼 너비 자동 조정
		autoFitColumns(sheet, exportColumns, rows)

		// 엑셀 파일 다운로드
		const buffer = await workbook.xlsx.writeBuffer()
		const blob = new Blob([buffer], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		})
		const url = window.URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = `균주별항생제통계_${moment().format('YYYYMMDD_HHmmss')}.xlsx`
		a.click()
		window.URL.revokeObjectURL(url)
	}

	// 내부사용자일 경우 거래처 선택 팝업
	const [clientDialogOpen, setClientDialogOpen] = useState(false)
	const [searchClientName, setSearchClientName] = useState('')
	const clientDialogClose = () => {
		setClientDialogOpen(false)
	}
	const clientPopupOpenAction = () => {
		setClientDialogOpen(true)
	}
	const clientPopupSubmit = (e: any) => {
		setSearchClientName(e.ClientName)
		setSearch({
			...search,
			clientID: e.ClientID,
		})
		setClientDialogOpen(false)
	}

	// calulate today before n months
	const getBeforeMonth = (n: number) => {
		setSearch({
			...search,
			startDate: moment().subtract(n, 'months').format('YYYY-MM'),
		})
	}

	// calulate today before 1 year
	const getBeforeYear = (n: number) => {
		setSearch({
			...search,
			startDate: moment().subtract(n, 'years').format('YYYY-MM'),
		})
	}

	const searchDateRanageOnChange = (event: any) => {
		switch (event.target.value) {
			case '1m':
				getBeforeMonth(1)
				break
			case '3m':
				getBeforeMonth(3)
				break
			case '6m':
				getBeforeMonth(6)
				break
			case '1y':
				getBeforeYear(1)
				break
			default:
				break
		}
		setDateRange(event.target.value)
		setDisabledSearch(false)
	}

	// date picker on change event
	const dateRanageOnChange = (e: any, target: string) => {
		if (moment(new Date(e)).isBefore('2023-08')) {
			alertModal('2023-08 이전 날짜는 조회할 수 없습니다.', 'error', () => {})
			setSearch({
				...search,
				startDate: '2023-08',
			})
			return
		} else {
			if (moment(new Date(e)).format('yyyy-MM').length === 7) {
				if (!isValidDateType(e)) {
					alertModal('날짜 형식이 올바르지 않습니다.', 'error', () => {})
					setDisabledSearch(true)
					return
				}

				if (target === 'startDate') {
					let diffValue = compareDate(e, search.endDate)
					if (diffValue > 0) {
						alertModal('시작일이 종료일보다 큽니다.', 'error', () => {})
						setDisabledSearch(true)
						return
					}
					if (
						moment(search.endDate, ['YYYY-MM', 'YYYY-MM-DD'], true)
							.startOf('month')
							.diff(moment(e).startOf('month'), 'months') > 12
					) {
						alertModal('검색기간은 1년을 초과할 수 없습니다.', 'error', () => {})
						setDisabledSearch(true)
						return
					}
				} else if (target === 'endDate') {
					let diffValue = compareDate(e, search.startDate)
					if (diffValue < 0) {
						alertModal('종료일이 시작일보다 작습니다.', 'error', () => {})
						setDisabledSearch(true)
						return
					}
					if (
						moment(e)
							.startOf('month')
							.diff(
								moment(search.startDate, ['YYYY-MM', 'YYYY-MM-DD'], true).startOf(
									'month',
								),
								'months',
							) > 12
					) {
						alertModal('검색기간은 1년을 초과할 수 없습니다.', 'error', () => {})
						setDisabledSearch(true)
						return
					}
				}

				setDisabledSearch(false)

				setSearch((prevState) => {
					return {
						...prevState,
						[target]: moment(new Date(e)).format('yyyy-MM'),
					}
				})
			} else {
				setDisabledSearch(true)
			}
		}
	}

	// grid
	const mstRef = useGridApiRef()
	const [rows, setRows] = useState<TransformedRowType[]>([])
	const [columns, setColumns] = useState<GridColDef[]>([
		{
			field: 'eqpMicroNm',
			headerName: '균주명',
			minWidth: 300,
			headerAlign: 'center',
			headerClassName: 'basic-info-header',
		},
		{
			field: 'microTotal',
			headerName: '전체건수',
			width: 100,
			headerAlign: 'center',
			type: 'number',
			headerClassName: 'basic-info-header last-basic-header',
		},
	])
	const [columnGroupingModel, setColumnGroupingModel] =
		useState<GridColumnGroupingModel>([
			{
				groupId: 'basicInfo',
				headerName: '기본정보',
				headerAlign: 'center',
				headerClassName: 'basic-header',
				children: [{ field: 'eqpMicroNm' }, { field: 'microTotal' }],
			},
		])
	const [gridMstLoading, setGridMstLoading] = useState(false)
	const [gridMstPageSize, setGridMstPageSize] = useState(env.row ? env.row : 100)
	const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([])

	useEffect(() => {
		if (userInfo?.infoType === 'C') actionSearch()
	}, [])

	return (
		<>
			<ClientSearchPopup
				dialogOpen={clientDialogOpen}
				dialogClose={clientDialogClose}
				popupSubmit={clientPopupSubmit}
			/>
			<Stack
				sx={{
					position: 'sticky',
					top: '50px',
					zIndex: 100,
					padding: '0.5rem',
					borderRadius: '0.5rem',
					backgroundColor:
						theme.palette.mode === 'dark' ? 'rgba(50, 48, 54, 1)' : '#fff',
					'@media (max-width: 600px)': {
						position: 'relative',
						paddingTop: '15px',
						top: '0px',
					},
				}}
			>
				<PageWrap style={{ marginBottom: '0.5rem' }}>
					<PageBg2 sx={{ padding: '0.3rem 0.3rem 0rem 0.5rem' }}>
						<Breadcrumbs
							separator={<NavigateNextIcon fontSize="small" />}
							aria-label="breadcrumb"
							sx={{
								'@media (max-width: 900px': {
									display: 'none',
								},
							}}
						>
							<TitleText
								key="3"
								color="text.primary"
								sx={{ fontWeight: '500', fontSize: 17 }}
							>
								균주별 항생제 통계
							</TitleText>
						</Breadcrumbs>
						<BtnBox>
							<div>
								<span>
									<Button
										size="small"
										color="success"
										variant="contained"
										startIcon={isMdUp && <RotateLeftIcon />}
										sx={{
											marginRight: '0.5rem',
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
										disabled={disabledReset}
										onClick={actionReset}
									>
										초기화
									</Button>
								</span>
								<span>
									<Button
										size="small"
										color="primary"
										variant="contained"
										startIcon={isMdUp && <ContentPasteSearchTwoToneIcon />}
										sx={{
											marginRight: '0.5rem',
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
										disabled={disabledSearch}
										onClick={actionSearch}
									>
										조회
									</Button>
								</span>
								<span>
									<Button
										variant="contained"
										color="secondary"
										size="small"
										startIcon={<DownloadIcon />}
										onClick={actionExcelDownload}
										sx={{
											marginRight: '0.5rem',
											padding: isMdUp ? '4px 10px' : '4px 6px',
											minWidth: isMdUp ? '64px' : '50px',
										}}
										disabled={rows.length === 0}
									>
										엑셀 다운로드
									</Button>
								</span>
							</div>
						</BtnBox>
					</PageBg2>
				</PageWrap>
				<Box
					component="form"
					noValidate
					autoComplete="off"
					onKeyUp={onKeyUp}
					sx={{
						borderTop:
							theme.palette.mode === 'dark'
								? '1px solid rgba(81, 81, 81, 1)'
								: '1px solid #ccc',
						paddingTop: '1rem',
					}}
				>
					<LocalizationProvider
						dateAdapter={AdapterDayjs}
						adapterLocale={locale}
						dateFormats={{
							monthAndYear: 'YYYY년 M월',
						}}
					>
						<SearchRowItem>
							<p className="labelText">조회조건</p>
							<Grid
								container
								spacing={1}
							>
								<Grid
									item
									xs={6}
									sm={3}
									md={3}
								>
									<FormControl fullWidth>
										<InputLabel id="searchType-select-label">기간 유형</InputLabel>
										<Select
											labelId="searchType-select-label"
											id="searchTypee-simple-select"
											value={search.searchType}
											label="기간 유형"
											onChange={(e) => {
												setSearch({
													...search,
													searchType: e.target.value,
												})
											}}
											size={'small'}
										>
											<MenuItem value={'R'}>접수일자</MenuItem>
											{/* <MenuItem value={'D'}>보고일자</MenuItem> */}
										</Select>
									</FormControl>
								</Grid>
								<Grid
									item
									xs={6}
									sm={3}
									md={3}
								>
									<DatePicker
										views={['month', 'year']}
										label="시작월"
										minDate={dayjs('2018-03-01')}
										maxDate={dayjs(new Date())}
										inputFormat="YYYY-MM"
										value={search.startDate}
										onChange={(newValue) => {
											dateRanageOnChange(newValue, 'startDate')
											// setSearch({
											// 	...search,
											// 	startDate: newValue
											// 		? newValue.format('YYYY-MM')
											// 		: search.startDate,
											// })
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												inputProps={{
													...params.inputProps,
													readOnly: true,
												}}
												helperText={null}
												size="small"
												fullWidth
											/>
										)}
									/>
								</Grid>
								<Grid
									item
									xs={6}
									sm={3}
									md={3}
								>
									<DatePicker
										views={['month', 'year']}
										label="종료월"
										minDate={dayjs('2018-03-01')}
										maxDate={dayjs(new Date())}
										inputFormat="YYYY-MM"
										value={search.endDate}
										onChange={(newValue) => {
											dateRanageOnChange(newValue, 'endDate')
											// setSearch({
											// 	...search,
											// 	endDate: newValue ? newValue.format('YYYY-MM') : search.endDate,
											// })
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												inputProps={{
													...params.inputProps,
													readOnly: true,
												}}
												helperText={null}
												size="small"
												fullWidth
											/>
										)}
									/>
								</Grid>
								<Grid
									item
									xs={6}
									sm={3}
									md={3}
								>
									<FormControl fullWidth>
										<InputLabel id="date-range-select-label">조회 기간</InputLabel>
										<Select
											labelId="date-range-select-label"
											id="date-range-simple-select"
											value={dateRange}
											label="조회 기간"
											onChange={searchDateRanageOnChange}
											size={'small'}
										>
											<MenuItem value={'0'}>선택하세요.</MenuItem>
											<MenuItem value={'1m'}>1개월</MenuItem>
											<MenuItem value={'3m'}>3개월</MenuItem>
											<MenuItem value={'6m'}>6개월</MenuItem>
											<MenuItem value={'1y'}>1년</MenuItem>
										</Select>
									</FormControl>
								</Grid>
								<Grid
									item
									xs={6}
									sm={3}
									md={3}
								>
									<FormControl fullWidth>
										<InputLabel id="strain-name-select-label">균명</InputLabel>
										<Select
											labelId="strain-name-select-label"
											id="strain-name-simple-select"
											value={search.eqpMicroNm}
											label="균명"
											size={'small'}
											onChange={(e) => {
												setSearch({
													...search,
													eqpMicroNm: e.target.value,
												})
											}}
										>
											<MenuItem value={'0'}>전체</MenuItem>
											{eqpMicroList.map((item, index) => (
												<MenuItem
													key={index}
													value={item.eqpMicroNm}
												>
													{item.eqpMicroNm}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid
									item
									xs={6}
									sm={3}
									md={3}
								>
									<FormControl fullWidth>
										<InputLabel id="antibiotic-name-select-label">항생제명</InputLabel>
										<Select
											labelId="antibiotic-name-select-label"
											id="antibiotic-name-simple-select"
											value={search.antiName}
											label="항생제명"
											size={'small'}
											onChange={(e) => {
												setSearch({
													...search,
													antiName: e.target.value,
												})
											}}
										>
											<MenuItem value={'0'}>전체</MenuItem>
											{antiNameList.map((item, index) => (
												<MenuItem
													key={index}
													value={item.antiName}
												>
													{item.antiName}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid
									item
									xs={6}
									sm={3}
									md={3}
								>
									<FormControl fullWidth>
										<InputLabel id="ris-name-select-label">감수성결과</InputLabel>
										<Select
											labelId="ris-name-select-label"
											id="ris-name-simple-select"
											value={search.risName}
											label="감수성결과"
											size={'small'}
											onChange={(e) => {
												setSearch({
													...search,
													risName: e.target.value,
												})
											}}
										>
											<MenuItem value={'0'}>전체</MenuItem>
											<MenuItem value={'S'}>S</MenuItem>
											<MenuItem value={'I'}>I</MenuItem>
											<MenuItem value={'R'}>R</MenuItem>
											<MenuItem value={'N'}>N</MenuItem>
											<MenuItem value={'P'}>P</MenuItem>
											<MenuItem value={'-'}>-</MenuItem>
											<MenuItem value={'.'}>.</MenuItem>
											{/* {risNameList.map((item, index) => (
												<MenuItem
													key={index}
													value={item.risName}
												>
													{item.risName}
												</MenuItem>
											))} */}
										</Select>
									</FormControl>
								</Grid>
								{(userInfo.infoType === 'S' || userInfo.infoType === 'W') && (
									<Grid
										item
										xs={6}
										sm={3}
										md={3}
									>
										<TextField
											label="거래처"
											variant="outlined"
											size="small"
											type="text"
											fullWidth
											aria-readonly={true}
											value={searchClientName}
											onClick={clientPopupOpenAction}
										/>
									</Grid>
								)}
							</Grid>
						</SearchRowItem>
					</LocalizationProvider>
				</Box>
			</Stack>
			<Paper
				elevation={0}
				sx={{ width: '100%', overflow: 'hidden', mt: 2 }}
			>
				<Grid container>
					<Grid
						item
						xs={12}
					>
						<PageBg>
							<Box
								sx={{
									height: 'calc(100vh - 300px)',
									'@media (max-width: 1199px)': {
										height: '400px',
										overflow: 'auto',
									},
								}}
							>
								<Box sx={{ height: '100%' }}>
									<DataGridPremium
										rows={rows}
										apiRef={mstRef}
										columns={columns}
										getRowId={(row) => row.id}
										pageSize={gridMstPageSize}
										onPageSizeChange={(newPageSize) => setGridMstPageSize(newPageSize)}
										rowsPerPageOptions={[5, 10, 20, 50, 100]}
										pagination
										autoHeight={false}
										loading={gridMstLoading}
										components={{
											Toolbar: GridToolbar,
											LoadingOverlay: LinearProgress,
										}}
										selectionModel={selectionModel}
										onSelectionModelChange={(newSelection) => {
											setSelectionModel(newSelection)
										}}
										localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
										componentsProps={{
											toolbar: {
												csvOptions: { disableToolbarButton: true },
												excelOptions: { disableToolbarButton: true },
												printOptions: { disableToolbarButton: true },
												showQuickFilter: false,
												quickFilterProps: { debounceMs: 250 },
											},
										}}
										density={(env.desnse as GridDensity) || 'compact'}
										initialState={{
											columns: {
												columnVisibilityModel: {
													id: false,
												},
											},
										}}
										experimentalFeatures={{ columnGrouping: true }}
										columnGroupingModel={columnGroupingModel}
										sx={{
											'& [class*="MuiDataGrid-columnHeaders"]': {
												// borderTop: '1px solid #95a5a6',
												// borderBottom: '1px solid #95a5a6',
											},
											'& .last-basic-header': { borderRight: '1px solid #95a5a6' },
											'& .biotic-header': {
												backgroundColor: '#bdc3c7',
												borderRight: '1px solid #95a5a6',
											},
											'& .dynamic-header': {
												backgroundColor:
													theme.palette.mode === 'dark'
														? 'rgba(81, 81, 81, 1)'
														: 'rgba(224, 224, 224, 1)',
												borderRight: '1px solid #95a5a6',
											},
											'& .MuiDataGrid-columnSeparator': {
												opacity: '0 !important',
												color: 'transparent !important',
											},
											'& .MuiDataGrid-columnHeaderTitleContainer': {
												borderBottom: '0 !important',
												fontSize: '13px',
											},
											'& .basic-header': {
												backgroundColor: '#7bbeeb',
											},
											'& .basic-info-header': {
												backgroundColor: '#a6dcff',
												'&:not(:last-child)': {
													borderRight: '1px solid #7bbeeb',
												},
											},
											'& .MuiDataGrid-cell': {
												fontSize: '13px !important',
												borderRight:
													theme.palette.mode === 'dark'
														? '1px solid rgba(81, 81, 81, 1)'
														: '1px solid rgba(224, 224, 224, 1)',
											},
										}}
									/>
								</Box>
							</Box>
						</PageBg>
					</Grid>
				</Grid>
			</Paper>
		</>
	)
}
export default ByStrain
